import { useId, useState } from 'react';
import { BuyingPurpose, computeBuyingCosts, JovemEligibility } from './buyingCosts';

const DEFAULT_PRICE = '250000';

const euros = (value: number) => `${new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(Math.round(value))} €`;
const toNumber = (text: string) => {
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

// Starts from a worked example so the prerendered HTML already shows real numbers.
export default function BuyingCostsCalculator() {
  const id = useId();
  const [price, setPrice] = useState(DEFAULT_PRICE);
  const [vpt, setVpt] = useState('');
  const [purpose, setPurpose] = useState<BuyingPurpose>('hpp');
  const [jovem, setJovem] = useState<JovemEligibility>('none');
  const [loan, setLoan] = useState('');

  const priceValue = toNumber(price);
  const costs = priceValue
    ? computeBuyingCosts({ price: priceValue, vpt: toNumber(vpt), purpose, jovem, loan: toNumber(loan) })
    : null;
  const rows: Array<[string, string]> = costs ? [
    ['IMT', euros(costs.imt)],
    ['Imposto do Selo da compra', euros(costs.stampDuty)],
    ...(costs.loanStampDuty > 0 ? [['Imposto do Selo do crédito', euros(costs.loanStampDuty)] as [string, string]] : []),
    ['Escritura e registos', `${euros(costs.deedMin)} a ${euros(costs.deedMax)}`],
  ] : [];

  const segments: Array<[string, number, string]> = costs ? [
    ['imt', costs.imt, 'IMT'],
    ['stamp', costs.stampDuty, 'Selo da compra'],
    ['loan', costs.loanStampDuty, 'Selo do crédito'],
    ['deed', costs.deedMax, 'Escritura e registos'],
  ] : [];

  return (
    <section className="guide-calculator" aria-labelledby={`${id}-title`} data-testid="buying-costs-calculator">
      <div className="guide-calculator-inputs">
      <h2 id={`${id}-title`}>Calcula o teu caso</h2>
      <form className="guide-calculator-form" onSubmit={(event) => event.preventDefault()}>
        <label htmlFor={`${id}-price`}>
          Preço da casa (€)
          <input id={`${id}-price`} type="number" inputMode="numeric" min="0" step="1000" value={price} onChange={(event) => setPrice(event.target.value)} />
        </label>
        <label htmlFor={`${id}-purpose`}>
          Para que é a casa?
          <select id={`${id}-purpose`} value={purpose} onChange={(event) => setPurpose(event.target.value as BuyingPurpose)}>
            <option value="hpp">Habitação própria e permanente</option>
            <option value="secondary">Segunda habitação ou arrendamento</option>
          </select>
        </label>
        <label htmlFor={`${id}-jovem`}>
          Quem compra tem direito a IMT Jovem?
          <select id={`${id}-jovem`} value={jovem} disabled={purpose !== 'hpp'} onChange={(event) => setJovem(event.target.value as JovemEligibility)}>
            <option value="none">Ninguém</option>
            <option value="all">Todos os compradores</option>
            <option value="some">Só alguns</option>
          </select>
        </label>
        <label htmlFor={`${id}-vpt`}>
          VPT do imóvel (€, opcional)
          <input id={`${id}-vpt`} type="number" inputMode="numeric" min="0" step="1000" value={vpt} onChange={(event) => setVpt(event.target.value)} />
        </label>
        <label htmlFor={`${id}-loan`}>
          Valor do crédito (€, opcional)
          <input id={`${id}-loan`} type="number" inputMode="numeric" min="0" step="1000" value={loan} onChange={(event) => setLoan(event.target.value)} />
        </label>
      </form>
      </div>
      <div aria-live="polite" className="guide-calculator-result">
        {costs ? (
          <>
            <div className="guide-cost-bar" aria-hidden="true">
              {segments.filter(([, value]) => value > 0).map(([key, value]) => <span key={key} className={`guide-cost-${key}`} style={{ flexGrow: value }} />)}
            </div>
            <ul className="zp-legend guide-cost-legend" aria-hidden="true">
              {segments.filter(([, value]) => value > 0).map(([key, , name]) => <li key={key}><span className={`zp-swatch guide-cost-${key}`} />{name}</li>)}
            </ul>
            <table>
              <caption>Estimativa para uma casa de {euros(priceValue as number)}, além da entrada</caption>
              <tbody>
                {rows.map(([label, value]) => <tr key={label}><th scope="row">{label}</th><td>{value}</td></tr>)}
              </tbody>
              <tfoot>
                <tr><th scope="row">Total</th><td>{euros(costs.totalMin)} a {euros(costs.totalMax)}</td></tr>
              </tfoot>
            </table>
            {costs.notes.map((note) => <p className="guide-calculator-note" key={note}>{note}</p>)}
          </>
        ) : <p>Indica o preço da casa para veres a estimativa.</p>}
        <p className="guide-calculator-scope">Estimativa para residentes fiscais no Continente, com as tabelas de 2026. Confirma com um solicitador. Sem VPT, usamos só o preço: o imposto incide sobre o valor mais alto dos dois.</p>
      </div>
    </section>
  );
}
